import { useMemo } from 'react'
import {
  isEmpty,
  type ProgramData,
  type ProgramId,
  type ProgramListInput,
  type ProgramListSuccess,
  type UserId,
  sortPrograms,
} from '@terros/common'
import { useTantalimQuery } from '../query'
import { type GetHookResponse, type WithEnabled } from '../hooks'
import { programListKey } from './keys'

export type UseProgramsProps = WithEnabled &
  ProgramListInput & {
    impersonateUserId?: UserId
  }

export function usePrograms({ enabled, impersonateUserId, ...input }: UseProgramsProps = {}): GetHookResponse<
  ProgramData[]
> {
  return useTantalimQuery<ProgramListInput, ProgramListSuccess, ProgramData[]>({
    url: '/program/list',
    queryKey: programListKey(isEmpty(input) ? undefined : input),
    input,
    convertResponse: (data) => sortPrograms(data.programs),
    options: {
      enabled,
      impersonateUserId,
    },
  })
}

export function useProgramsMap(input?: UseProgramsProps): GetHookResponse<Map<ProgramId, ProgramData>> {
  const { data: programs, ...state } = usePrograms(input)
  const map = useMemo(
    () =>
      programs?.reduce((acc, program) => {
        acc.set(program.programId, program)
        return acc
      }, new Map<ProgramId, ProgramData>()),
    [programs]
  )
  return { ...state, data: map }
}
